import { CalendarOutlined, WarningFilled } from "@ant-design/icons"
import {
  App,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Row,
  Tooltip
} from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import type { RootState } from "../../../../../../../store.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import type { ProjectMilestoneFilterOptions } from "../../../../../redux/project.types.ts"
import type { ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { MILESTONE_STATUSES } from "../../../../constants/milestone-statuses.ts"
import { completedActualEndDefault } from "../../../../utils/milestone-completed-actual-end.ts"
import { isFinishedLate } from "../../../../utils/milestone-schedule-state.ts"
import { milestoneEffortRules } from "../../../../utils/milestone-effort.ts"
import { PROJECT_ENTITY_CATEGORIES } from "../../../../constants/project-entity-category.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { PlannedMoneyInput } from "../../../../../components/PlannedMoneyInput.tsx"
import { TextArea } from "../../../../../../../components/Input/TextArea.tsx"
import { renderMilestoneStatusOption } from "./MilestoneStatusTag.tsx"
import {
  type MilestoneSaveResult,
  useMilestoneScheduleFields
} from "./use-milestone-schedule-fields.ts"

interface ProjectMilestoneModalProps {
  open: boolean
  options: ProjectMilestoneFilterOptions
  onClose: () => void
  onSubmit: (data: ProjectMilestone) => Promise<MilestoneSaveResult>
}

const DEFAULT_VALUES: Partial<ProjectMilestone> = {
  name: "",
  status: MILESTONE_STATUSES.PLANNED,
  description: "",
  planned_receivable: 0,
  start_date: null,
  end_date: null,
  duration_days: null,
  actual_end_date: null,
  milestone_category: PROJECT_ENTITY_CATEGORIES.STANDARD
}

export const ProjectMilestoneModal = ({
  open,
  options,
  onClose,
  onSubmit
}: ProjectMilestoneModalProps) => {
  const { t } = useTranslation()
  const {
    getDateValueProps,
    createDateRangeRules,
    normalizeDate,
    parseDate,
    userFormat
  } = useDateFormat()
  const accountTimezone = useSelector(
    (state: RootState) => state.auth.userInfo?.timezone
  )
  // Typed dates parse in the account's format first, ISO second; without an
  // explicit format antd only ever parses what the picker locale says.
  const pickerFormat = userFormat ? [userFormat, DATE_FORMAT] : [DATE_FORMAT]
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectMilestone>()
  const { modal } = App.useApp()
  const schedule = useMilestoneScheduleFields(form, normalizeDate)

  const watch = Form.useWatch(
    (s) => ({
      status: s?.status,
      actualEnd: s?.actual_end_date,
      plannedEnd: s?.end_date
    }),
    form
  )

  const isCompleted = watch?.status === MILESTONE_STATUSES.COMPLETED
  const completedWithoutActualEnd = isCompleted && !watch?.actualEnd
  const isLate = isFinishedLate(watch?.actualEnd, watch?.plannedEnd, parseDate)

  const handleSubmit = async () => {
    try {
      const values = await validateFormWithScroll(form)

      setSaving(true)

      const res = await onSubmit({
        ...schedule.withEditedScheduleOnly(values),
        actual_end_date: normalizeDate(values?.actual_end_date)
      } as ProjectMilestone)
      if (!res?.ok) {
        schedule.showServerErrors(res)
        return
      }

      form.resetFields()
      onClose()
    } catch (error: any) {
      if (!error?.errorFields) throw error
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const isChanged = isFormChanged<ProjectMilestone>(
      form.getFieldsValue(),
      DEFAULT_VALUES as ProjectMilestone
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          form.resetFields()
          onClose()
        }
      })
    } else {
      form.resetFields()
      onClose()
    }
  }

  const onStatusChangeHandler = (value: number) => {
    if (value === MILESTONE_STATUSES.COMPLETED) {
      if (!form.getFieldValue("actual_end_date")) {
        form.setFieldValue(
          "actual_end_date",
          completedActualEndDefault(
            parseDate(form.getFieldValue("end_date")),
            accountTimezone
          )
        )
      }
    }
  }

  return (
    <Modal
      open={open}
      title={t("projectMilestones.modal.title")}
      okText={t("projectMilestones.modal.ok")}
      cancelText={t("projectMilestones.modal.cancel")}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={saving}
      destroyOnHidden
      width={600}
      maskClosable={false}
      keyboard={false}
    >
      <Form
        form={form}
        layout="vertical"
        disabled={saving}
        className="mt-4"
        initialValues={DEFAULT_VALUES}
        validateTrigger={["onSubmit", "onChange"]}
      >
        {/* Milestone Name */}
        <Form.Item<ProjectMilestone>
          name="name"
          label={t("projectMilestones.form.name")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("projectMilestones.form.nameRequired")
            },
            { max: 255, message: t("projectMilestones.form.nameMax") }
          ]}
        >
          <TextArea
            autoSize={{ minRows: 1, maxRows: 3 }}
            placeholder={t("projectMilestones.form.namePlaceholder")}
            autoFocus
          />
        </Form.Item>

        <Row>
          <Col sm={24} md={16}>
            <Tooltip
              title={
                completedWithoutActualEnd
                  ? t("projectMilestones.tooltips.completedNeedsActualEnd")
                  : ""
              }
              color="orange"
            >
              <Form.Item<ProjectMilestone>
                name="status"
                label={t("projectMilestones.form.status")}
                rules={[
                  {
                    required: true,
                    message: t("projectMilestones.form.required")
                  }
                ]}
              >
                <ResolvedSelect
                  status={completedWithoutActualEnd ? "warning" : ""}
                  placeholder={t("projectMilestones.form.statusPlaceholder")}
                  options={options?.["status"]}
                  onChange={onStatusChangeHandler}
                  optionRender={renderMilestoneStatusOption}
                />
              </Form.Item>
            </Tooltip>
          </Col>
          <Col sm={24} md={8}>
            <Form.Item<ProjectMilestone>
              name="milestone_category"
              label={t("projectMilestones.form.type")}
            >
              <ResolvedSelect
                placeholder={t("projectMilestones.form.typePlaceholder")}
                options={options?.["category"]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4">
          {/* Planned Start */}
          <Form.Item<ProjectMilestone>
            name="start_date"
            label={t("projectMilestones.form.plannedStart")}
            getValueProps={getDateValueProps}
            dependencies={["end_date"]}
            rules={createDateRangeRules<ProjectMilestone>("end_date", "start")}
          >
            <DatePicker
              format={pickerFormat}
              placeholder={t("projectMilestones.form.datePlaceholder")}
              className="w-full"
              onChange={(value) => schedule.onEdit("start_date", value)}
            />
          </Form.Item>

          {/* Planned End */}
          <Form.Item<ProjectMilestone>
            name="end_date"
            label={t("projectMilestones.form.plannedEnd")}
            getValueProps={getDateValueProps}
            dependencies={["start_date"]}
            rules={createDateRangeRules<ProjectMilestone>("start_date", "end", {
              end: t("projectMilestones.form.endBeforeStart")
            })}
          >
            <DatePicker
              format={pickerFormat}
              placeholder={t("projectMilestones.form.datePlaceholder")}
              className="w-full"
              onChange={(value) => schedule.onEdit("end_date", value)}
            />
          </Form.Item>

          {/* Effort */}
          <Form.Item<ProjectMilestone>
            name="duration_days"
            label={t("projectMilestones.form.effort")}
            tooltip={t("projectMilestones.tooltips.effort")}
            rules={milestoneEffortRules(t("projectMilestones.form.effortMin"))}
          >
            <InputNumber
              className="w-full"
              precision={0}
              placeholder={t("projectMilestones.form.effortPlaceholder")}
              onChange={(value) => schedule.onEdit("duration_days", value)}
            />
          </Form.Item>

          {/* Actual End */}
          <Tooltip
            title={
              completedWithoutActualEnd
                ? t("projectMilestones.tooltips.completedNeedsActualEnd")
                : ""
            }
            color="orange"
          >
            <Form.Item<ProjectMilestone>
              name="actual_end_date"
              label={t("projectMilestones.form.actualEnd")}
              getValueProps={getDateValueProps}
            >
              <DatePicker
                format={pickerFormat}
                status={completedWithoutActualEnd ? "warning" : ""}
                placeholder={t("projectMilestones.form.datePlaceholder")}
                className="w-full"
                suffixIcon={
                  isLate ? (
                    <WarningFilled className="text-warning" />
                  ) : (
                    <CalendarOutlined />
                  )
                }
              />
            </Form.Item>
          </Tooltip>
        </div>

        {/* Planned Receivable */}
        <Tooltip title={t("projectMilestones.tooltips.plannedReceivable")}>
          <PlannedMoneyInput
            name="planned_receivable"
            label={t("projectMilestones.form.plannedReceivable")}
          />
        </Tooltip>

        {/* Planned Payable */}
        <Tooltip title={t("projectMilestones.tooltips.plannedPayable")}>
          <PlannedMoneyInput
            name="planned_payable"
            label={t("projectMilestones.form.plannedPayable")}
          />
        </Tooltip>

        {/* Description */}
        <Form.Item<ProjectMilestone>
          name="description"
          label={t("projectMilestones.form.description")}
          rules={[
            {
              whitespace: true,
              max: 250,
              message: t("projectMilestones.form.descriptionMax")
            }
          ]}
        >
          <TextArea
            rows={3}
            autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder={t("projectMilestones.form.descriptionPlaceholder")}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
